import Log from '@deephaven/log';
import { isWidgetPlugin, isWidgetMiddlewarePlugin } from '@deephaven/plugin';
var log = Log.module('WidgetLoaderUtils');

/**
 * Information about a widget type including its base plugin and any middleware.
 */

/**
 * Widget type info that is guaranteed to have a base plugin.
 */

/**
 * Build a map of widget types to their plugin chain info.
 * For each type, we have a base plugin and a list of middleware to apply.
 *
 * Types that only have middleware registered (no base plugin) are omitted.
 *
 * @param plugins Map of all registered plugin modules
 * @returns Map of widget type to the base plugin and middleware for that type
 */
export function getSupportedWidgetTypes(plugins) {
  var typeMap = new Map();
  plugins.forEach(plugin => {
    var isMiddleware = isWidgetMiddlewarePlugin(plugin);
    if (!isWidgetPlugin(plugin) && !isMiddleware) {
      return;
    }
    [plugin.supportedTypes].flat().forEach(supportedType => {
      if (supportedType == null || supportedType === '') {
        return;
      }
      var existing = typeMap.get(supportedType);
      if (isMiddleware) {
        // Add middleware to existing chain or create pending chain
        if (existing != null) {
          existing.middleware.push(plugin);
          log.debug("Adding middleware ".concat(plugin.name, " to chain for type ").concat(supportedType));
        } else {
          // No base plugin yet, create entry with just middleware
          // The base plugin will be set when a non-middleware plugin is registered
          typeMap.set(supportedType, {
            basePlugin: null,
            middleware: [plugin]
          });
          log.debug("Creating pending middleware chain for type ".concat(supportedType, " with ").concat(plugin.name));
        }
      } else {
        // Non-middleware plugin: becomes the base plugin
        if (existing != null) {
          if (existing.basePlugin != null) {
            // Already have a base plugin, warn about replacement
            log.warn("Multiple WidgetPlugins handling type ".concat(supportedType, ". ") + "Replacing ".concat(existing.basePlugin.name, " with ").concat(plugin.name, " as base plugin"));
          }
          // Keep existing middleware, update the base plugin
          existing.basePlugin = plugin;
        } else {
          typeMap.set(supportedType, {
            basePlugin: plugin,
            middleware: []
          });
        }
        log.debug("Set base plugin ".concat(plugin.name, " for type ").concat(supportedType));
      }
    });
  });

  // Filter out entries that only have middleware (no base plugin)
  var validEntries = new Map();
  typeMap.forEach((info, type) => {
    if (info.basePlugin != null) {
      validEntries.set(type, info);
    } else {
      log.warn("No base plugin found for type ".concat(type, ", middleware will not be applied"));
    }
  });
  return validEntries;
}

/**
 * Get the unique base plugins from a supported types map, merging the middleware
 * from each type a given base plugin handles.
 *
 * @param supportedTypes Map of widget type to base plugin and middleware
 * @returns Map of base plugin name to the base plugin and its merged middleware
 */
export function getUniqueWidgetPluginInfos(supportedTypes) {
  var uniquePluginInfos = new Map();
  supportedTypes.forEach(info => {
    var existingInfo = uniquePluginInfos.get(info.basePlugin.name);
    if (existingInfo == null) {
      // Clone so the source map is not mutated
      uniquePluginInfos.set(info.basePlugin.name, {
        basePlugin: info.basePlugin,
        middleware: [...info.middleware]
      });
      return;
    }

    // Merge middleware from multiple type registrations for the same base plugin
    info.middleware.forEach(m => {
      if (!existingInfo.middleware.includes(m)) {
        existingInfo.middleware.push(m);
      }
    });
  });
  return uniquePluginInfos;
}
//# sourceMappingURL=WidgetLoaderUtils.js.map